import React, { useEffect, useRef, useState } from 'react';
import { Stack, Image, Row, Col, Tab, Tabs, Form } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetSeekerFilteredDataQuery, useMultiSearchQuery } from 'services';
import { IMAGE_PATHS } from 'constants/imagePaths';
import PaginationBox from 'components/PaginationBox';
import { useQueryClient } from '@tanstack/react-query';
import ReactSelect from 'react-select';
import CandidateDetailBox from 'components/Common/Candidates/CandidateDetailBox';
import useTranslate from 'hooks/useTranslate';
import MembersSkeleton from 'components/Common/Skeleton/MembersSkeleton';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import useSkeleton from 'hooks/useSkeleton';
import useAuthStore from 'store/auth';
import { S3_URL, USER_TYPES } from 'constants/index';
import FollowBtn from 'components/Common/ActionButton/FollowBtn';
import CandidateChips from 'components/Common/Candidates/CandidateChips';
import HashtagTile from 'pages/SocialFeed/MembersResult/HashtagTile';
import '../styles.scss';

const MembersResult: React.FC = () => {
	const { translate } = useTranslate();
	const [searchParams] = useSearchParams();
	const { userType = '' }: any = useAuthStore((state) => state?.userInfo?.user);
	const salaryRef = useRef<any>(null);
	const keyword = searchParams.get('keyword')?.replace(/^#/, '');
	const navigate = useNavigate();
	const [page, setPage] = useState(1);
	const [searchType, setSearchType] = useState('hashtag');
	const [memberQuery, setMemberQuery] = useState({
		levelId: '',
		salaryType: '',
		salary: '',
		skill: '',
		jobRoles: '',
		cityId: '',
		stateId: '',
		countryId: '',
		workMode: '',
		industry: '',
	});
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const delayedLoading = useSkeleton(isLoading);
	const { data: { docs = [], meta = {} } = {}, refetch } = useMultiSearchQuery({
		page,
		searchType,
		keyword,
		memberQuery,
	});
	const queryClient = useQueryClient();

	const {
		data: { jobRoles = [], levels = [], salary: salaryArr = [], skill = [], industries = [] } = {},
	} = useGetSeekerFilteredDataQuery();

	const salaryTypeArr = Object.keys(salaryArr).map((key) => ({
		value: key,
	}));

	useEffect(() => {
		setIsLoading(true);
		queryClient.invalidateQueries({ queryKey: [searchType] }).finally(() => {
			setIsLoading(false);
		});
	}, [
		keyword,
		memberQuery.levelId,
		memberQuery.salary,
		memberQuery.salaryType,
		memberQuery.jobRoles,
		memberQuery.skill,
		memberQuery.countryId,
		memberQuery.stateId,
		memberQuery.cityId,
		memberQuery.workMode,
		memberQuery.industry,
	]);

	const handleCurrentPage = (pageNo: number) => {
		setPage(pageNo);
	};

	const handleTabChange = (k: any) => {
		setPage(1);
		setSearchType(k);
		setIsLoading(true);
		queryClient.invalidateQueries({ queryKey: [k] }).finally(() => {
			setIsLoading(false);
		});
	};

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'instant',
		});
		setIsLoading(true);
		queryClient.invalidateQueries({ queryKey: [searchType] }).finally(() => {
			setIsLoading(false);
		});
	}, [page]);

	const onHandleChange = (name: string, id: any) => {
		setPage(1);
		if (name === 'skill') {
			const ids = id?.map((item: any) => item.id).join(',');
			setMemberQuery((prev) => ({ ...prev, [name]: ids }));
		} else if (name === 'salaryType') {
			salaryRef?.current?.clearValue();
			setMemberQuery((prev) => ({ ...prev, [name]: id || '', salary: '' }));
		} else {
			setMemberQuery((prev) => ({ ...prev, [name]: id || '' }));
		}
	};

	const goToDetailPage = (id: any) => {
		navigate(`/hashtag/Profile/${id}`);
	};

	return (
		<div className="social-feed">
			<div className="social-feed-left">
				<div className="members-result white-box bg-white">
					<Tabs
						defaultActiveKey="hashtag"
						id="uncontrolled-tab-example"
						onSelect={(k: any) => {
							handleTabChange(k);
						}}
					>
						<Tab eventKey="hashtag" title={translate('Social.hashtags')}>
							<Row className="members-result-inner">
								{delayedLoading ? (
									<MembersSkeleton />
								) : (
									<>
										{docs?.length > 0 ? (
											docs?.map((res: any, index: any) => (
												<Col key={`${index}-${res?.id}-${searchType}`} xxl={4} xl={6} md={4} sm={6}>
													<HashtagTile res={res} goToDetailPage={goToDetailPage} />
												</Col>
											))
										) : (
											<Stack className="posted-job-box">
												<p className="mb-0">{translate('Common.noHashtagFound')} </p>
											</Stack>
										)}
									</>
								)}
							</Row>
						</Tab>
						<Tab eventKey="company" title={translate('Social.business')}>
							<Row className="members-result-inner">
								{delayedLoading ? (
									<MembersSkeleton />
								) : (
									<>
										{docs?.length > 0 ? (
											docs?.map((res: any, index: any) => (
												<Col
													key={`${index}-${searchType}-${res?.userId}`}
													xxl={4}
													xl={6}
													md={4}
													sm={6}
												>
													<Stack className="business-box">
														<div
															className="business-box-logo"
															onClick={() => {
																navigate(`/company/profile/${res?.userId}`);
															}}
														>
															<Image
																src={`${S3_URL}${res?.profilePicture}`}
																onError={({ currentTarget }) => {
																	currentTarget.onerror = null;
																	currentTarget.src = IMAGE_PATHS.JobLogo1;
																}}
																alt="logo"
																className="w-100 h-100"
																loading="lazy"
															/>
														</div>
														<h6
															style={{ cursor: 'pointer' }}
															className="fw-bold mb-0"
															onClick={() => {
																navigate(`/company/profile/${res?.userId}`);
															}}
														>
															{res?.companyName}
														</h6>
														<div className="">
															<p className="business-type mb-0">{res?.industryName}</p>
															<p className="business-location mb-1">{res?.address}</p>
														</div>
														<FollowBtn
															isFollowed={res?.isFollowed}
															id={res?.userId}
															follow={'comp'}
															show={true}
														/>
													</Stack>
												</Col>
											))
										) : (
											<Stack className="posted-job-box">
												<p className="mb-0">{translate('Common.noCompanyFound')} </p>
											</Stack>
										)}
									</>
								)}
							</Row>
						</Tab>

						<Tab
							eventKey="member"
							title={`${userType === USER_TYPES.COMPANY ? translate('Social.candidates') : translate('Social.members')}`}
						>
							<div className="filtered-jobs-filters d-flex flex-column flex-xl-row align-items-xl-center">
								{userType === USER_TYPES.COMPANY && (
									<div className="filters-list d-flex flex-wrap">
										<Form.Group className="position-relative">
											<ReactSelect
												isSearchable
												isClearable
												name="salaryType"
												options={salaryTypeArr}
												getOptionValue={(option) => option.value}
												getOptionLabel={(option) => option.value}
												onChange={(optionValue: any) =>
													onHandleChange('salaryType', optionValue?.value)
												}
												autoFocus={false}
												placeholder={translate('PlaceHolder.salaryType')}
												className="form-react-select "
												classNamePrefix="select"
											/>
										</Form.Group>

										{Object.keys(salaryArr)?.includes(memberQuery.salaryType) && (
											<Form.Group className="position-relative">
												<ReactSelect
													isSearchable
													isClearable
													name="salary"
													options={salaryArr[memberQuery.salaryType] || []}
													getOptionValue={(option) => option.id}
													getOptionLabel={(option) => option.value}
													onChange={(optionValue: any) => onHandleChange('salary', optionValue?.id)}
													autoFocus={false}
													placeholder={translate('PlaceHolder.salaryRange')}
													className="form-react-select "
													classNamePrefix="select"
													value={salaryArr[memberQuery?.salaryType]?.find(
														(item: any) => item.id === Number(memberQuery.salary),
													)}
													isOptionDisabled={(item: any) => item.id === Number(memberQuery.salary)}
													ref={salaryRef}
												/>
											</Form.Group>
										)}

										<Form.Group className="position-relative">
											<ReactSelect
												isSearchable
												isClearable
												name="jobRoles"
												options={industries}
												getOptionValue={(option) => option.id}
												getOptionLabel={(option) => option.title}
												onChange={(optionValue: any) => onHandleChange('industry', optionValue?.id)}
												autoFocus={false}
												className="form-react-select "
												classNamePrefix="select"
												placeholder={translate('PlaceHolder.industry')}
											/>
										</Form.Group>

										<Form.Group className="position-relative">
											<CustomSearchAddress
												selectProps={{
													placeholder: translate('PlaceHolder.locationText'),
													onChange: (value: any) => {
														setMemberQuery((prev) => ({
															...prev,
															cityId: value?.cityId || '',
															stateId: value?.stateId || '',
															countryId: value?.countryId || '',
														}));
													},
													name: 'location',
												}}
												isClearable={true}
											/>
										</Form.Group>

										<Form.Group className="position-relative">
											<ReactSelect
												isSearchable
												name="skills"
												isMulti={true}
												options={skill}
												getOptionValue={(option) => option.id}
												getOptionLabel={(option) => option.title}
												onChange={(optionValue: any) => onHandleChange('skill', optionValue)}
												autoFocus={false}
												placeholder={translate('PlaceHolder.skills')}
												className="form-react-select "
												classNamePrefix="select"
												isClearable={false}
											/>
										</Form.Group>

										<Form.Group className="position-relative">
											<ReactSelect
												isSearchable
												isClearable
												name="levelId"
												options={levels}
												getOptionValue={(option) => option.id}
												getOptionLabel={(option) => option.title}
												onChange={(optionValue: any) => onHandleChange('levelId', optionValue?.id)}
												autoFocus={false}
												className="form-react-select "
												classNamePrefix="select"
												placeholder={translate('PlaceHolder.experience')}
											/>
										</Form.Group>
										<Form.Group className="position-relative">
											<ReactSelect
												isSearchable
												isClearable
												name="jobRoles"
												options={jobRoles}
												getOptionValue={(option) => option.id}
												getOptionLabel={(option) => option.parentName}
												onChange={(optionValue: any) => onHandleChange('jobRoles', optionValue?.id)}
												autoFocus={false}
												className="form-react-select "
												classNamePrefix="select"
												placeholder={translate('PlaceHolder.jobRoles')}
											/>
										</Form.Group>
									</div>
								)}
							</div>
							<Row className="members-result-inner">
								{delayedLoading ? (
									<MembersSkeleton />
								) : (
									<>
										{docs?.length > 0 &&
											(userType === USER_TYPES?.COMPANY
												? docs?.map((res: any, index: any) => (
														<CandidateDetailBox
															key={`${index}-${res?.name}-${searchType}-${res?.userId}`}
															applicantList={res}
															userId={res?.userId}
															actions={false}
															refetch={refetch}
														/>
													))
												: docs?.map((res: any, index: any) => (
														<CandidateChips
															key={`${index}-${res?.name}-${searchType}-32${res?.userId}`}
															indexKey={`${index}-${res?.name}-${searchType}-${res?.userId}`}
															userId={res?.userId}
															applicantList={res}
														/>
													)))}
										{docs?.length === 0 && (
											<Stack className="posted-job-box">
												<p className="mb-0">{translate('Common.noMemberFound')} </p>
											</Stack>
										)}
									</>
								)}
							</Row>
						</Tab>
					</Tabs>

					{meta?.totalDocs > meta?.limit && (
						<PaginationBox
							totalData={meta?.totalDocs}
							limit={meta?.limit}
							page={page}
							setCurrentPage={handleCurrentPage}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default MembersResult;
